import { createChannelClient, createChannelHost } from './channel'

// Singleton wrappers for the channel client (iframe side) and host
// (parent side). Multiple bundles loaded into the same window share
// one channel instance — e.g. resizeIframe.js and the main widget
// React app both call getChannelClient() and get the same handshake.
// Construction is side-effectful (the client posts CONNECT on
// instantiation), so a single shared instance is the only correct
// shape; per-bundle instances would each kick off their own handshake
// and the parent would only ack one of them.
//
// Sharing crosses bundle boundaries via the window object — each
// bundle is its own JS context with its own module-scope variables,
// so the singleton key has to live somewhere both bundles can see.

const CLIENT_KEY = '__plgChannelClient'
const HOST_KEY   = '__plgChannelHost'

// Sibling window keys that hold a JSON snapshot of the options the
// singleton was constructed with, so a later getter call that passes
// divergent options can surface a warning instead of silently using
// the original. Construction happens once — there is no path to
// retroactively apply a different timeoutMs / supportedVersions etc.
const CLIENT_OPTIONS_KEY = '__plgChannelClientOptions'
const HOST_OPTIONS_KEY   = '__plgChannelHostOptions'

// Warn when a repeat singleton call passes options that differ from
// the ones the singleton was constructed with. Calls that pass no
// options aren't claiming anything (typical inheritance pattern from
// a later bundle), so they don't warn.
const warnDivergent = (name, storedJson, options) => {
  if (options === undefined) return
  const incoming = JSON.stringify(options)
  if (incoming === storedJson) return
  console.warn(
    `${name}: ignoring divergent options on repeat call — the singleton was `
    + `constructed earlier and cannot be reconfigured. Initial: `
    + `${storedJson ?? '(none)'}. Ignored: ${incoming}.`
  )
}

// Console message shown when the handshake times out — typically
// because the parent page hasn't loaded our embed script. Matches the
// text intersectingIframe.js used to log before this protocol existed.
const EMBED_WARNING = [
  '[Pledge.to] This widget is embedded incorrectly.',
  'This can result in reduced functionality.',
  [
    'Learn how to embed our widgets with our embed script',
    'https://developer.pledge.to/widgets/embed-script/',
  ].join(': '),
].join('\n\n')

// Iframe-side: returns the client singleton, creating it on first
// call. The handshake fires at creation; subsequent callers just
// attach handlers via .on(). Options are read on first call only —
// later calls return the existing instance regardless of args.
export const getChannelClient = (options) => {
  if (window[CLIENT_KEY]) {
    warnDivergent('getChannelClient', window[CLIENT_OPTIONS_KEY], options)
    return window[CLIENT_KEY]
  }
  const client = createChannelClient(options)
  // The catch body is the feature, not a no-op swallow: when the
  // handshake fails (parent didn't load our embed script), we log the
  // "widget embedded incorrectly" warning. channel.js has its own
  // default `.catch(() => {})` for direct callers (e.g. unit tests
  // that don't opt into `.ready`); this one is the singleton's
  // opt-in.
  client.ready.catch(() => console.warn(EMBED_WARNING))
  window[CLIENT_KEY] = client
  window[CLIENT_OPTIONS_KEY] = options === undefined ? undefined : JSON.stringify(options)
  return client
}

// Parent-side: returns the host singleton, creating it on first call.
// The host's window-level CONNECT listener registers at creation, so
// any subsequent iframe.contentWindow handshakes are caught.
export const getChannelHost = (options) => {
  if (window[HOST_KEY]) {
    warnDivergent('getChannelHost', window[HOST_OPTIONS_KEY], options)
    return window[HOST_KEY]
  }
  window[HOST_KEY] = createChannelHost(options)
  window[HOST_OPTIONS_KEY] = options === undefined ? undefined : JSON.stringify(options)
  return window[HOST_KEY]
}
